import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import HubSpotChat from "../adaptive-homepage-hub/components/HubSpotChat";
import CaseStudyCard from "./components/CaseStudyCard";
import CategoryFilter from "./components/CategoryFilter";
import FeaturedCaseStudy from "./components/FeaturedCaseStudy";
import ProcessTimeline from "./components/ProcessTimeline";
import ResultsShowcase from "./components/ResultsShowcase";
import BehindTheScenes from "./components/BehindTheScenes";

const CaseStudiesShowcase = () => {
  // Open HubSpot Conversations (chat)
  const openHubSpotChat = () => {
    try {
      window.hsConversationsSettings = window.hsConversationsSettings || {
        loadImmediately: true,
      };
      if (window.HubSpotConversations && window.HubSpotConversations.widget) {
        window.HubSpotConversations.widget.open();
        return;
      }
      window.hsConversationsOnReady = [
        () => window.HubSpotConversations?.widget?.open?.(),
      ];
    } catch (e) {
      // no-op
    }
  };

  // Open HubSpot Meetings overlay
  const openHubSpotMeeting = () => {
    if (!document.getElementById("MeetingsEmbedCode-js")) {
      const s = document.createElement("script");
      s.src =
        "https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js";
      s.async = true;
      s.defer = true;
      s.id = "MeetingsEmbedCode-js";
      document.body.appendChild(s);
    }
    let overlay = document.getElementById("hs-meetings-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "hs-meetings-overlay";
      overlay.className =
        "fixed inset-0 z-[60] flex justify-center items-start p-0 bg-[#425B76] md:bg-black/50";
      overlay.style.overflow = "hidden";
      overlay.style.overscrollBehavior = "contain";
      const inner = document.createElement("div");
      inner.className =
        "relative w-screen h-screen md:w-full md:max-w-4xl md:h-[90vh] bg-[#425B76] md:bg-transparent rounded-none shadow-none p-0";
      inner.style.overflow = "hidden";
      const close = document.createElement("button");
      close.className =
        "fixed top-4 right-4 md:absolute md:top-[16px] md:right-[-3px] z-[70] text-gray-500 hover:text-gray-900 bg-white hover:bg-white/80 rounded";
      close.innerText = "×";
      close.style.fontSize = "20px";
      close.style.padding = "2px 9px";
      close.style.paddingBottom = "6px";
      close.onclick = () => {
        document.body.style.overflow = "";
        overlay.remove();
      };
      const iframe = document.createElement("iframe");
      iframe.src =
        "https://meetings-na2.hubspot.com/brian-wilkinson?embed=true";
      iframe.style.width = "100%";
      iframe.style.height = "100%";
      iframe.style.minWidth = "312px";
      iframe.style.minHeight = "0";
      iframe.style.border = "none";
      iframe.style.overflow = "hidden";
      iframe.style.overscrollBehavior = "contain";
      iframe.style.display = "block";
      iframe.setAttribute("scrolling", "no");
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (isMobile) {
        iframe.style.position = "absolute";
        iframe.style.width = "110vw";
        iframe.style.height = "110vh";
        iframe.style.left = "-5vw";
        iframe.style.top = "-5vh";
      } else {
        iframe.style.position = "static";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.left = "0";
        iframe.style.top = "0";
      }
      inner.appendChild(iframe);
      inner.appendChild(close);
      overlay.appendChild(inner);
      document.body.appendChild(overlay);
      document.body.style.overflow = "hidden";
    }
  };

  const [activeCategory, setActiveCategory] = useState("all");
  const [currentTheme, setCurrentTheme] = useState("default");

  // Mock data for categories
  const categories = [
    {
      id: "all",
      name: "All Projects",
      icon: "Grid3X3",
      count: 24,
      activeClass: "bg-gray-800",
    },
    {
      id: "events",
      name: "Events & Weddings",
      icon: "Heart",
      count: 8,
      activeClass: "bg-rose-600",
    },
    {
      id: "realestate",
      name: "Real Estate",
      icon: "Building",
      count: 6,
      activeClass: "bg-blue-600",
    },
    {
      id: "marketing",
      name: "Marketing",
      icon: "Megaphone",
      count: 7,
      activeClass: "bg-orange-600",
    },
    {
      id: "design",
      name: "Design Fiction",
      icon: "Sparkles",
      count: 3,
      activeClass: "bg-emerald-600",
    },
  ];

  // Mock data for featured case study
  const featuredStudy = {
    title: "Luxury Estate Marketing Revolution",
    description:
      "How we transformed a struggling luxury property into a market sensation through cinematic storytelling and strategic visual marketing, resulting in a 40% faster sale at 15% above asking price.",
    category: "Real Estate",
    duration: "3 weeks",
    icon: "Building",
    image:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop",
    testimonial:
      "Skycrest didn't just create a video - they crafted a story that made buyers fall in love with the property before they even visited. The results speak for themselves.",
    client: {
      name: "Sarah Mitchell",
      title: "Luxury Real Estate Agent",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
    },
    metrics: [
      { value: "40%", label: "Faster Sale" },
      { value: "15%", label: "Above Asking" },
      { value: "2.3M", label: "Views Generated" },
    ],
    awards: true,
  };

  // Mock data for case studies
  const caseStudies = [
    {
      id: 1,
      title: "Enchanted Garden Wedding",
      description:
        "A romantic celebration captured through cinematic storytelling, showcasing the couple's love story against breathtaking natural backdrops.",
      category: "Events",
      industry: "Wedding Videography",
      duration: "8 min",
      icon: "Heart",
      image:
        "https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop",
      slug: "enchanted-garden-wedding",
      client: {
        name: "Emma & James",
        location: "Napa Valley, CA",
        logo: "https://randomuser.me/api/portraits/women/25.jpg",
      },
      metrics: [
        { value: "500K", label: "Views" },
        { value: "98%", label: "Satisfaction" },
        { value: "50+", label: "Referrals" },
      ],
    },
    {
      id: 2,
      title: "Modern Penthouse Showcase",
      description:
        "Architectural precision meets luxury living in this stunning property tour that generated unprecedented buyer interest.",
      category: "Real Estate",
      industry: "Property Marketing",
      duration: "4 min",
      icon: "Building",
      image:
        "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop",
      slug: "modern-penthouse-showcase",
      client: {
        name: "Elite Properties",
        location: "Manhattan, NY",
        logo: "https://randomuser.me/api/portraits/men/45.jpg",
      },
      metrics: [
        { value: "1.2M", label: "Views" },
        { value: "35%", label: "Faster Sale" },
        { value: "12%", label: "Price Premium" },
      ],
    },
    {
      id: 3,
      title: "Tech Startup Brand Story",
      description:
        "Bold visual storytelling that transformed a complex AI product into an engaging narrative, driving 300% increase in user signups.",
      category: "Marketing",
      industry: "Brand Marketing",
      duration: "2 min",
      icon: "Megaphone",
      image:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop",
      slug: "tech-startup-brand-story",
      client: {
        name: "NeuralTech AI",
        location: "San Francisco, CA",
        logo: "https://randomuser.me/api/portraits/men/38.jpg",
      },
      metrics: [
        { value: "300%", label: "Signup Increase" },
        { value: "2.8M", label: "Impressions" },
        { value: "8.5%", label: "CTR" },
      ],
    },
    {
      id: 4,
      title: "Future City Concept",
      description:
        "Visionary exploration of sustainable urban living, blending cutting-edge design with environmental consciousness.",
      category: "Design Fiction",
      industry: "Conceptual Design",
      duration: "6 min",
      icon: "Sparkles",
      image:
        "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=300&fit=crop",
      slug: "future-city-concept",
      client: {
        name: "Urban Futures Lab",
        location: "Copenhagen, DK",
        logo: "https://randomuser.me/api/portraits/women/42.jpg",
      },
      metrics: [
        { value: "1.5M", label: "Views" },
        { value: "85%", label: "Engagement" },
        { value: "3", label: "Awards Won" },
      ],
    },
    {
      id: 5,
      title: "Corporate Gala Highlights",
      description:
        "Elegant documentation of a Fortune 500 company's annual celebration, capturing key moments and executive presentations.",
      category: "Events",
      industry: "Corporate Events",
      duration: "12 min",
      icon: "Heart",
      image:
        "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=300&fit=crop",
      slug: "corporate-gala-highlights",
      client: {
        name: "GlobalTech Corp",
        location: "Chicago, IL",
        logo: "https://randomuser.me/api/portraits/men/55.jpg",
      },
      metrics: [
        { value: "800K", label: "Views" },
        { value: "95%", label: "Client Rating" },
        { value: "25", label: "Media Mentions" },
      ],
    },
    {
      id: 6,
      title: "Luxury Resort Portfolio",
      description:
        "Comprehensive visual marketing suite for a premium resort chain, showcasing amenities and experiences across multiple properties.",
      category: "Real Estate",
      industry: "Hospitality Marketing",
      duration: "15 min",
      icon: "Building",
      image:
        "https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400&h=300&fit=crop",
      slug: "luxury-resort-portfolio",
      client: {
        name: "Paradise Resorts",
        location: "Maldives",
        logo: "https://randomuser.me/api/portraits/women/35.jpg",
      },
      metrics: [
        { value: "3.2M", label: "Views" },
        { value: "45%", label: "Booking Increase" },
        { value: "92%", label: "Guest Satisfaction" },
      ],
    },
  ];

  // Mock data for process timeline
  const processSteps = [
    {
      title: "Discovery & Strategy",
      description:
        "Deep dive into client goals, target audience, and project requirements to develop a comprehensive creative strategy.",
      duration: "1-2 days",
      icon: "Search",
      deliverables: [
        "Creative brief and strategy document",
        "Shot list and storyboard concepts",
        "Timeline and production schedule",
      ],
      tools: ["Notion", "Figma", "Miro"],
    },
    {
      title: "Pre-Production Planning",
      description:
        "Detailed planning phase covering location scouting, equipment preparation, and team coordination.",
      duration: "2-3 days",
      icon: "Calendar",
      deliverables: [
        "Location permits and arrangements",
        "Equipment checklist and backup plans",
        "Team roles and responsibilities",
      ],
      tools: ["Google Maps", "Weather Apps", "Equipment Database"],
    },
    {
      title: "Production & Filming",
      description:
        "On-location filming with professional crew, capturing all planned shots and adapting to real-time conditions.",
      duration: "1-5 days",
      icon: "Camera",
      deliverables: [
        "Raw footage in multiple formats",
        "Behind-the-scenes documentation",
        "Daily production reports",
      ],
      tools: ["RED Cameras", "DJI Drones", "Professional Audio Equipment"],
    },
    {
      title: "Post-Production Magic",
      description:
        "Expert editing, color grading, and sound design to bring the vision to life with cinematic quality.",
      duration: "1-2 weeks",
      icon: "Edit",
      deliverables: [
        "Final edited video in multiple formats",
        "Color-graded and audio-mastered versions",
        "Thumbnail and promotional assets",
      ],
      tools: ["DaVinci Resolve", "Adobe Premiere", "Pro Tools"],
    },
    {
      title: "Delivery & Optimization",
      description:
        "Final delivery with platform-specific optimizations and ongoing support for maximum impact.",
      duration: "1-2 days",
      icon: "Upload",
      deliverables: [
        "Platform-optimized video files",
        "Usage rights and licensing documentation",
        "Performance tracking setup",
      ],
      tools: ["Vimeo", "YouTube Studio", "Analytics Platforms"],
    },
  ];

  // Mock data for results showcase
  const resultsData = {
    metrics: [
      { icon: "Eye", value: "2.5M+", label: "Total Views", change: 45 },
      {
        icon: "TrendingUp",
        value: "35%",
        label: "Avg. Engagement",
        change: 22,
      },
      { icon: "Award", value: "12", label: "Awards Won", change: 15 },
      { icon: "Users", value: "98%", label: "Client Satisfaction", change: 8 },
    ],
    comparison: {
      before: {
        image:
          "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop",
        points: [
          "Low engagement rates",
          "Generic visual content",
          "Limited brand recognition",
          "Poor conversion metrics",
        ],
      },
      after: {
        image:
          "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=300&h=200&fit=crop",
        points: [
          "300% increase in engagement",
          "Distinctive brand storytelling",
          "Industry recognition and awards",
          "Measurable ROI improvement",
        ],
      },
    },
    feedback: {
      name: "Michael Chen",
      title: "Marketing Director",
      avatar: "https://randomuser.me/api/portraits/men/28.jpg",
      quote:
        "Skycrest transformed our entire approach to visual marketing. The results exceeded every expectation we had.",
    },
  };

  // Mock data for behind the scenes
  const behindTheScenesData = {
    process: {
      description:
        "Our creative process combines strategic thinking with artistic vision, ensuring every frame serves the story and business objectives.",
      steps: [
        "Client consultation and goal alignment",
        "Creative concept development",
        "Storyboard and shot planning",
        "Location scouting and preparation",
        "Production execution with real-time adaptation",
      ],
      images: [
        {
          src: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=300&h=200&fit=crop",
          alt: "Planning session",
        },
        {
          src: "https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?w=300&h=200&fit=crop",
          alt: "Equipment setup",
        },
      ],
    },
    equipment: {
      items: [
        {
          name: "RED Komodo 6K",
          category: "Primary Camera",
          icon: "Camera",
          description:
            "Professional cinema camera for stunning 6K resolution and exceptional dynamic range.",
          specs: ["6K Resolution", "16.5 Stops DR", "120fps Capability"],
        },
        {
          name: "DJI Inspire 2",
          category: "Aerial Cinematography",
          icon: "Plane",
          description:
            "Professional drone for breathtaking aerial shots and smooth cinematic movements.",
          specs: ["4K/60fps", "360° Rotation", "Obstacle Avoidance"],
        },
        {
          name: "Sennheiser MKH 416",
          category: "Audio Recording",
          icon: "Mic",
          description:
            "Industry-standard shotgun microphone for crystal-clear audio capture.",
          specs: ["Directional Pattern", "Low Noise", "Weather Resistant"],
        },
      ],
    },
    challenges: {
      items: [
        {
          problem: "Unpredictable Weather Conditions",
          description:
            "Outdoor shoot faced sudden weather changes that threatened the production schedule.",
          solution: "Flexible Backup Planning",
          outcome:
            "Successfully adapted with indoor alternatives and rescheduled key outdoor scenes, maintaining quality while meeting deadlines.",
        },
        {
          problem: "Complex Technical Requirements",
          description:
            "Client needed simultaneous multi-angle coverage of a live event with no retakes possible.",
          solution: "Multi-Camera Coordination System",
          outcome:
            "Deployed 8-camera setup with real-time monitoring, capturing every crucial moment from multiple perspectives.",
        },
      ],
    },
    team: {
      members: [
        {
          name: "Alex Rodriguez",
          role: "Director of Photography",
          avatar: "https://randomuser.me/api/portraits/men/32.jpg",
          insight:
            "The key to this project was understanding that we weren't just filming a property - we were crafting an emotional experience that would resonate with potential buyers.",
          skills: ["Cinematography", "Lighting Design", "Color Theory"],
        },
        {
          name: "Sarah Kim",
          role: "Creative Director",
          avatar: "https://randomuser.me/api/portraits/women/28.jpg",
          insight:
            "Every creative decision was driven by data and client objectives, ensuring beautiful visuals that also delivered measurable business results.",
          skills: [
            "Creative Strategy",
            "Brand Storytelling",
            "Project Management",
          ],
        },
      ],
    },
  };

  // Filter case studies based on active category
  const filteredStudies =
    activeCategory === "all"
      ? caseStudies
      : caseStudies?.filter((study) =>
          study?.category?.toLowerCase()?.includes(activeCategory)
        );

  // Update theme based on active category
  useEffect(() => {
    setCurrentTheme(activeCategory === "all" ? "default" : activeCategory);
  }, [activeCategory]);

  const getThemeClasses = () => {
    switch (currentTheme) {
      case "events":
        return {
          hero: "from-rose-50 via-pink-50 to-rose-100",
          accent: "text-rose-600",
          button: "bg-rose-600 hover:bg-rose-700",
        };
      case "realestate":
        return {
          hero: "from-blue-50 via-slate-50 to-blue-100",
          accent: "text-blue-600",
          button: "bg-blue-600 hover:bg-blue-700",
        };
      case "marketing":
        return {
          hero: "from-orange-50 via-red-50 to-orange-100",
          accent: "text-orange-600",
          button: "bg-orange-600 hover:bg-orange-700",
        };
      case "design":
        return {
          hero: "from-emerald-50 via-teal-50 to-emerald-100",
          accent: "text-emerald-600",
          button: "bg-emerald-600 hover:bg-emerald-700",
        };
      default:
        return {
          hero: "from-gray-50 via-white to-gray-100",
          accent: "text-primary",
          button: "bg-primary hover:bg-primary/90",
        };
    }
  };

  const themeClasses = getThemeClasses();

  return (
    <>
      <Helmet>
        <title>
          Case Studies - Skycrest Studios | Proven Results Across All Industries
        </title>
        <meta
          name="description"
          content="Explore in-depth case studies showcasing Skycrest Studios' problem-solving approach and measurable results across events, real estate, marketing, and design fiction projects."
        />
        <meta
          name="keywords"
          content="video production case studies, wedding videography results, real estate marketing success, brand storytelling ROI, design fiction projects"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Ensure HubSpot chat script is available on this page */}
        <HubSpotChat />
        <Header />

        {/* Hero Section */}
        <section
          className={`pt-40 pb-16 bg-gradient-to-br ${themeClasses?.hero}`}
        >
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Proven Results That
                <span className={`block ${themeClasses?.accent} font-display`}>
                  Speak for Themselves
                </span>
              </h1>

              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
                Dive deep into our most impactful projects and discover how
                strategic visual storytelling drives measurable business results
                across every industry we serve.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  className={`${themeClasses?.button} text-white`}
                  iconName="Play"
                  iconPosition="left"
                >
                  Watch Success Stories
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>
        </section>

        {/* Featured Case Study */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <FeaturedCaseStudy study={featuredStudy} />
          </div>
        </section>

        {/* Case Studies Grid */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Explore Our Portfolio
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Each project tells a unique story of creative problem-solving
                and measurable success
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredStudies?.map((study) => (
                <CaseStudyCard
                  key={study?.id}
                  study={study}
                  theme={currentTheme}
                />
              ))}
            </div>

            {filteredStudies?.length === 0 && (
              <div className="text-center py-16">
                <Icon
                  name="Search"
                  size={48}
                  className="text-gray-400 mx-auto mb-4"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No case studies found
                </h3>
                <p className="text-gray-600">
                  Try selecting a different category to explore our work
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Process Timeline */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Our Proven Process
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  From initial concept to final delivery, every project follows
                  our refined methodology that ensures exceptional results
                </p>
              </div>

              <ProcessTimeline steps={processSteps} theme={currentTheme} />
            </div>
          </div>
        </section>

        {/* Results Showcase */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <ResultsShowcase results={resultsData} theme={currentTheme} />
            </div>
          </div>
        </section>

        {/* Behind the Scenes */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <BehindTheScenes
                content={behindTheScenesData}
                theme={currentTheme}
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-16 bg-gradient-to-br ${themeClasses?.hero}`}>
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Ready to Create Your Success Story?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Let's discuss how we can help you achieve similar results with
                strategic visual storytelling tailored to your unique goals and
                audience.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  className={`${themeClasses?.button} text-white`}
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={openHubSpotMeeting}
                >
                  Schedule Strategy Call
                </Button>
                <Button
                  variant="outline"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={openHubSpotChat}
                >
                  Start a Conversation
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer (white background, brand logo style) */}
        <footer className="bg-background text-foreground py-12 border-t border-border/50">
          <div className="container mx-auto px-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-2xl font-brand text-primary">
                  <span>Skycrest</span>
                  <span className="text-accent">Films</span>
                </span>
              </div>

              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Transforming visions into visual masterpieces that drive real
                business results. Every project is a testament to our commitment
                to excellence and innovation.
              </p>

              <div className="flex justify-center space-x-6 mb-6">
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Instagram" size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Youtube" size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Linkedin" size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-accent transition-colors"
                >
                  <Icon name="Twitter" size={20} />
                </a>
              </div>

              <div className="border-t border-border/50 pt-6">
                <p className="text-sm text-muted-foreground">
                  © {new Date()?.getFullYear()} SkycrestFilms. All rights
                  reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default CaseStudiesShowcase;
